import localDb from './local-db';
import { supportAsyncStorage, isServiceWorker, isBrowser } from './device-type';

export type SlidingKeyValueCache<TCacheItem> = {
  getFastCacheItem: (key: string) => TCacheItem | null;
  getFastCache: () => { [key: string]: TCacheItem };
  getCacheItem: (key: string) => Promise<TCacheItem | null>;
  setCacheItem: (item: TCacheItem, fastCacheOnly?: boolean) => Promise<unknown>;
  clearCacheItem: (key: string) => Promise<unknown>;
  nukeCache: () => Promise<unknown>;
};

export default function create<TCacheItem>(
  name: string,
  maxCacheSize: number,
  maxFastCacheSize: number,
  findIn: (cacheItem: TCacheItem, key: string) => TCacheItem | null,
  shouldCache: (item: TCacheItem) => boolean,
  shouldOverwriteCache: (existing: TCacheItem, newItem: TCacheItem) => boolean,
  shouldCacheInFastCache: (item: TCacheItem) => boolean,
  getKey: (item: TCacheItem) => string,
  allowFastCacheRemoval: (key: string) => boolean = key => true,
): SlidingKeyValueCache<TCacheItem> {
  const storage = localDb(name);
  const keysByDateKey = 'keys-by-date';

  // We keep a small cache in memory of full pages so that we can respond quicker
  // for the pages the user just viewed. This is mainly because when you go back
  // in the browser history, the browser will restore scroll position and if we
  // don't respond fast enough from the cache we haven't rendered the page when
  // the browser restores scroll position. Which means that the scroll position
  // will first jump, then we render, then the scroll position changes again.
  const fastCache: Cache = {};
  let fastCacheKeys: string[] = [];
  type Cache = { [key: string]: TCacheItem };

  let allCacheKeys: string[] | null = null;
  function getAllCacheKeys() {
    // This is a fun little thingy. The problem with keeping a "module global"
    // variable like `allCacheKeys` is how we keep it in sync. There's code down below in
    // this file which updates the `allCacheKeys` variable when we add new things to the cache.
    // BUT! The code that adds things to the cache will _run on the main thread_ and
    // not _on the service worker thread_. Which means that the `allCacheKeys` variable
    // in the service worker will be stale, and won't get updated until you restart your
    // browser basically.
    // Because of this we never load that list in the SW. The reason for loading that list
    // in the first place is to know if we should ask the cache at all, since the cache
    // can be slow and timeout in certain situations. The worst thing that can happen in the SW
    // is that we'll query the cache even if we know that the thing we ask for doesn't exist there.
    // In the best of worlds, the cache will respond in a couple of ms with either a cached item or
    // null. In the worst of worlds, the cache will timeout and respond with null after the timeout
    // period which is 100ms.
    if (isServiceWorker()) {
      return null;
    }

    if (!allCacheKeys) {
      storage.getItem<string[]>(keysByDateKey).then(keys => {
        if (keys) {
          // It's important that we only set allCachedUrls if we have a value for it
          // and not a default value. The storage is buggy sometimes and there's a difference
          // between knowing that the list is empty, and setting it to an empty list as
          // a default
          allCacheKeys = keys;
        } else {
          // This is wierd and a bug in Chrome, but it's possible to first ask for a key and get
          // nothing back, and then a bit later get a value for it.
          setTimeout(async () => {
            keys = await storage.getItem<string[]>(keysByDateKey);
            if (keys) {
              allCacheKeys = keys;
            }
          }, 1000);
        }
      });
    }
    return allCacheKeys as string[];
  }

  function findInFastCache(key: string) {
    for (let i = fastCacheKeys.length - 1; i >= 0; i--) {
      const partial = findIn(fastCache[fastCacheKeys[i]], key);
      if (partial) {
        return partial;
      }
    }
    return null;
  }

  function placeFirstInFastCache(item: TCacheItem) {
    if (isServiceWorker()) {
      return;
    }

    const key = getKey(item);
    fastCacheKeys = fastCacheKeys.filter(cachedKey => cachedKey !== key);
    fastCacheKeys.push(key);
  }

  let flushTimer: number;
  let pendingNewKeys: string[] = [];

  function onCacheSet(key: string) {
    if (!pendingNewKeys.includes(key)) {
      pendingNewKeys.unshift(key);

      clearTimeout(flushTimer);
      flushTimer = setTimeout(saveCacheKeysList, 100);
    }
  }

  function saveCacheKeysList() {
    storage.getItem<string[]>(keysByDateKey).then(keys => {
      keys = keys || [];
      keys = keys.filter(key => !pendingNewKeys.includes(key));

      keys = pendingNewKeys.concat(keys);
      pendingNewKeys = [];

      if (keys.length > maxCacheSize) {
        const keysToRemove = keys.slice(maxCacheSize - 1);
        keys = keys.slice(0, maxCacheSize - 1);
        keysToRemove.forEach(key => {
          storage.removeItem(key);
          console.info('Removing', key, 'from the cache');
        });
      }

      allCacheKeys = keys;
      storage.setItem(keysByDateKey, keys).then(
        () => {
          if (pendingNewKeys.length > 0) {
            clearTimeout(flushTimer);
            flushTimer = setTimeout(saveCacheKeysList, 100);
          }
        },
        e => {
          console.error('Error saving cache keys', e);
        },
      );
    });
  }

  function nukeCache() {
    Object.keys(fastCache).forEach(url => delete fastCache[url]);

    fastCacheKeys = [];
    return storage.clear().catch(() => null);
  }

  if (isBrowser()) {
    // tslint:disable:no-string-literal
    if (!(window as any)['nukeCaches']) {
      (window as any)['nukeCaches'] = {};
    }
    (window as any)['nukeCaches'][name] = nukeCache;
    // tslint:enable:no-string-literal
  }

  function addToFastCache(item: TCacheItem) {
    if (isServiceWorker()) {
      // We don't want an in-memory cache in the service worker, we only
      // want to load from the persistent database
      return;
    }
    if (shouldCacheInFastCache(item)) {
      if (fastCacheKeys.length > maxFastCacheSize) {
        for (let i = fastCacheKeys.length - 1; i >= 0; i--) {
          const oldestKeyInFastCache = fastCacheKeys[i];
          // Sometimes things in the fast cache aren't used frequently but still can't be dropped
          // because it can cause flickering. Such as data that is needed during the whole session.
          if (oldestKeyInFastCache && allowFastCacheRemoval(oldestKeyInFastCache)) {
            console.info('Dropping', oldestKeyInFastCache, 'from fast cache');
            fastCacheKeys.splice(i, 1);
            delete fastCache[oldestKeyInFastCache];
            break;
          }
        }
      }
      placeFirstInFastCache(item);
      fastCache[getKey(item)] = item;
    }
  }

  return {
    getFastCacheItem(key: string) {
      if (key in fastCache) {
        return fastCache[key];
      } else {
        return null;
      }
    },
    getFastCache() {
      return fastCache;
    },
    getCacheItem(key: string): Promise<TCacheItem | null> {
      if (!supportAsyncStorage) {
        return Promise.resolve(null);
      }
      if (fastCache[key]) {
        console.info('Loading', key, 'from fast cache');
        // If you request something from the fast queue
        // we want to update the priority list so we don't
        // drop something from the fast cache that was recently used
        placeFirstInFastCache(fastCache[key]);
        return Promise.resolve(fastCache[key]);
      }
      const keys = getAllCacheKeys();
      if (keys && keys.indexOf(key) === -1) {
        return Promise.resolve(findInFastCache(key));
      }
      return (storage
        .getItem<TCacheItem>(key)
        .then(
          item => {
            if (item) {
              addToFastCache(item);
            }
            return item;
          },
          () => {
            // If the storage is full, we just act like we don't have anything
            // in the cache
            return null;
          },
        )
        .then((cacheItem: any) => {
          if (!cacheItem) {
            return findInFastCache(key);
          }
          return cacheItem;
        }) as any) as Promise<TCacheItem>;
    },
    setCacheItem(item: TCacheItem, fastCacheOnly = false): Promise<any> {
      addToFastCache(item);

      if (!supportAsyncStorage) {
        return Promise.resolve(null);
      }
      if (!shouldCache(item)) {
        console.info('Not caching ', item);
        return Promise.resolve(null);
      }

      if (fastCacheOnly) {
        return Promise.resolve(null);
      }

      const key = getKey(item);

      return storage.getItem<TCacheItem>(key).then(existing => {
        if (!existing || shouldOverwriteCache(existing, item)) {
          onCacheSet(getKey(item));

          // We don't want to remove unserializable properties from the object
          // because the sender might use them after calling this method
          const serializable: TCacheItem = {} as TCacheItem;
          (Object.keys(item) as Array<keyof typeof item>).forEach(property => {
            // Because we can't serialize functions
            if (!(item[property] instanceof Function)) {
              serializable[property] = item[property];
            }
          });
          return storage
            .setItem(getKey(item), serializable)
            .catch(e => console.error('Error storing in item cache', item, e));
        } else {
          console.info('Not overwriting cache for ' + getKey(item));
        }
        return Promise.resolve(null);
      });
    },
    clearCacheItem(key: string) {
      const index = fastCacheKeys.findIndex(k => k === key);

      if (index !== -1) {
        fastCacheKeys.splice(index, 1);
        delete fastCache[key];
      }
      return storage.removeItem(key);
    },
    nukeCache,
  };
}
